/**
 * Demo sandbox — local-only mode for a shareable demo login.
 *
 * When a demo account is logged in, NO write ever reaches the server: mutations
 * are intercepted and stored in this browser's localStorage, and list reads merge
 * those local creations back in. So one demo login can be handed to many
 * prospects: each one's tinkering lives only in their browser, never touches the
 * DB, and no two prospects see each other's data. Read-only reference data still
 * comes from the server.
 *
 * Detection is by account email (DEMO_EMAILS) so you just create the account and
 * share the credentials — no flag for the prospect to set.
 */
import axios, { type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';

export const DEMO_EMAILS = ['demo@hazeasy.com'];
const STORE_KEY = 'hazeasy_demo_sandbox';
const EXIT_KEY = 'hazeasy_demo_exited';

// Core-flow resources that are fully sandboxed (create -> see -> open -> act).
const SANDBOXED = ['generators', 'waste-profiles', 'customers', 'pickup-schedules', 'field-jobs'];
// Never sandbox auth — the demo user must really log in / refresh tokens.
const PASSTHROUGH_PREFIXES = ['auth', 'v1/sds', 'onboarding'];

export function isDemoUser(): boolean {
  try {
    const auth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
    const email = String(auth?.state?.user?.email || '').toLowerCase();
    return DEMO_EMAILS.includes(email);
  } catch {
    return false;
  }
}
export function isDemoMode(): boolean {
  return isDemoUser() && sessionStorage.getItem(EXIT_KEY) !== '1';
}
export function exitDemo() { sessionStorage.setItem(EXIT_KEY, '1'); }
export function clearSandbox() { localStorage.removeItem(STORE_KEY); }

function loadStore(): Record<string, any[]> {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}'); } catch { return {}; }
}
function saveStore(s: Record<string, any[]>) { localStorage.setItem(STORE_KEY, JSON.stringify(s)); }

function pathOf(url = ''): string { return url.split('?')[0].replace(/^\//, ''); }
function resourceKey(url = ''): string | null {
  const seg = pathOf(url).split('/')[0];
  return SANDBOXED.includes(seg) ? seg : null;
}
function isPassthrough(url = ''): boolean {
  const p = pathOf(url);
  return PASSTHROUGH_PREFIXES.some((pre) => p.startsWith(pre));
}
function uid() { return 'demo-' + Math.random().toString(36).slice(2, 10); }

// Append demo-created items into whatever array we can find in a list response.
function mergeList(data: any, items: any[]): any {
  if (!items.length) return data;
  if (Array.isArray(data)) return [...items, ...data];
  if (data && typeof data === 'object') {
    if (Array.isArray(data.data)) return { ...data, data: [...items, ...data.data] };
    for (const k of Object.keys(data)) {
      if (Array.isArray(data[k])) return { ...data, [k]: [...items, ...data[k]] };
      if (data[k] && Array.isArray(data[k]?.data)) {
        return { ...data, [k]: { ...data[k], data: [...items, ...data[k].data] } };
      }
    }
  }
  return data;
}

function synthetic(config: InternalAxiosRequestConfig, body: any, status = 200): AxiosResponse {
  return { data: body, status, statusText: 'OK', headers: {}, config, request: {} } as AxiosResponse;
}
async function realRequest(config: InternalAxiosRequestConfig): Promise<AxiosResponse> {
  // Use the bare axios so we don't re-enter this adapter (no loop).
  return axios.request({ ...config, adapter: undefined as any });
}

/** Custom axios adapter installed only in demo mode. */
export async function demoAdapter(config: InternalAxiosRequestConfig): Promise<AxiosResponse> {
  const method = (config.method || 'get').toLowerCase();
  const url = config.url || '';

  if (isPassthrough(url)) return realRequest(config);

  const key = resourceKey(url);
  const path = pathOf(url);
  const idPart = path.split('/')[1];
  let body: any = {};
  try { body = config.data ? (typeof config.data === 'string' ? JSON.parse(config.data) : config.data) : {}; } catch { body = {}; }

  const store = loadStore();
  const list = key ? (store[key] || []) : [];

  // ---- sandboxed resource writes (local only) ----
  if (key && method === 'post' && !idPart) {
    const item = { id: uid(), _demo: true, created_at: new Date().toISOString(), status: body.status || 'pending_review', ...body };
    store[key] = [item, ...list]; saveStore(store);
    return synthetic(config, { success: true, data: item }, 201);
  }
  if (key && (method === 'put' || method === 'patch') && idPart) {
    if (idPart.startsWith('demo-')) {
      store[key] = list.map((it) => (it.id === idPart ? { ...it, ...body } : it)); saveStore(store);
      return synthetic(config, { success: true, data: store[key].find((it) => it.id === idPart) });
    }
    return synthetic(config, { success: true, data: { id: idPart, ...body, _demo: true } });
  }
  if (key && method === 'delete' && idPart) {
    if (idPart.startsWith('demo-')) { store[key] = list.filter((it) => it.id !== idPart); saveStore(store); }
    return synthetic(config, { success: true });
  }
  if (key && method === 'post' && idPart) {
    // sub-action (e.g. /:id/status, /:id/classify) — pretend it worked
    return synthetic(config, { success: true, data: { id: idPart, ...body } });
  }
  // ---- sandboxed resource reads ----
  if (key && method === 'get') {
    if (idPart && idPart.startsWith('demo-')) {
      return synthetic(config, { success: true, data: list.find((it) => it.id === idPart) || {} });
    }
    const real = await realRequest(config);
    if (!idPart) (real as any).data = mergeList(real.data, list);
    return real;
  }

  // ---- everything else ----
  if (method === 'get') return realRequest(config); // reference data, dashboards
  // Any other write in demo mode is blocked from the DB and faked successful.
  return synthetic(config, { success: true, _demoBlocked: true });
}
